import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import LeadDrawer from "@/components/LeadDrawer";
import ImportLeadsDialog from "@/components/ImportLeadsDialog";
import SmartImportDialog from "@/components/SmartImportDialog";
import { Pagination } from "@/components/Pagination";
import { ShipmentStatusDialog } from "@/components/ShipmentStatusDialog";
import { WorkflowStatus } from "@/components/WorkflowStatus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Shuffle, UserCheck, MessageCircle, Navigation, Truck, AlertTriangle, Link2 } from "lucide-react";
import { toast } from "sonner";

const trackingStatus = (l) => {
  if (!l.tracking_token) return { label: "Inactive", cls: "text-muted-foreground border-border" };
  const loc = l.driver_location;
  if (!loc || loc.lat == null) return { label: "Waiting for Permission", cls: "text-amber-600 dark:text-amber-400 border-amber-400" };
  const fresh = loc.updated_at && (Date.now() - new Date(loc.updated_at).getTime()) < 15 * 60 * 1000;
  return fresh
    ? { label: "Active", cls: "text-emerald-600 dark:text-emerald-400 border-emerald-400" }
    : { label: "Active (stale)", cls: "text-amber-600 dark:text-amber-400 border-amber-400" };
};

const waLink = (l) => {
  const url = `${window.location.origin}/wq/${l.share_token}`;
  const msg = `*Arrow Logistics* — Quote request for Order ${l.order_number}${l.lr_number ? ` (LR ${l.lr_number})` : ""}.\nTrip: ${l.pickup_city} → ${l.delivery_city}.\nSubmit your best rate here: ${url}`;
  return `https://wa.me/?text=${encodeURIComponent(msg)}`;
};

const STATUSES = ["all", "pending", "not_confirmed", "quote_required", "followup", "not_picked"];
const hasClaim = (l) => !!(l.damage_status || l.damage_claim || Number(l.damage_amount) > 0);

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function LeadsPage() {
  const { user } = useAuth();
  const isManager = user?.role === "admin" || user?.role === "manager";
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(monthStart());
  const [endDate, setEndDate] = useState(todayStr());
  const [selected, setSelected] = useState([]);
  const [execs, setExecs] = useState([]);
  const [assignTo, setAssignTo] = useState("");
  const [sp, setSp] = useSearchParams();
  const [openId, setOpenId] = useState(sp.get("open"));
  useEffect(() => {
    if (sp.get("open")) setSp({}, { replace: true });
  }, []); // eslint-disable-line
  const [focusStep, setFocusStep] = useState(null);
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);
  const [statusFor, setStatusFor] = useState(null);
  const [claimFilter, setClaimFilter] = useState(false);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (status !== "all") p.set("status", status);
    if (search) p.set("search", search);
    if (startDate) p.set("start_date", startDate);
    if (endDate) p.set("end_date", endDate);
    api.get(`/leads?${p}`).then((r) => setLeads(r.data));
  }, [status, search, startDate, endDate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [leads, claimFilter]);
  useEffect(() => { if (isManager) api.get("/users/executives").then((r) => setExecs(r.data)); }, [isManager]);

  const shown = claimFilter ? leads.filter(hasClaim) : leads;
  const paged = shown.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalArticleValue = shown.reduce((s, l) => s + (Number(l.article_value) || 0), 0);
  const fmtInr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allChecked = leads.length > 0 && selected.length === leads.length;

  const bulkAssign = async () => {
    if (!assignTo || selected.length === 0) return toast.error("Pick executive & leads");
    try { const { data } = await api.post("/leads/assign", { lead_ids: selected, executive_id: assignTo }); toast.success(`Assigned ${data.assigned} to ${data.executive}`); setSelected([]); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const autoAssign = async () => {
    try { const { data } = await api.post("/leads/auto-assign"); toast.success(data.message || `Round-robin assigned ${data.assigned} leads`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const copyQuickLink = () => {
    const link = `${window.location.origin}/quick-lead`;
    navigator.clipboard.writeText(link).then(() => toast.success("Quick-Lead link copied — share it to capture leads")).catch(() => toast.error("Copy failed — link: " + link));
  };

  const sendTracking = async (l) => {
    if (!(l.driver_mobile || "").trim()) return toast.error("Add a driver mobile number in the order first (open the order)");
    try {
      const r = await api.post(`/leads/${l.id}/request-tracking`);
      const link = `${window.location.origin}/drive/${r.data.tracking_token}`;
      const digits = String(l.driver_mobile).replace(/\D/g, "");
      const num = digits.length === 10 ? "91" + digits : digits;
      const msg = `*Arrow Logistics* — Live location tracking for Order ${l.order_number}${l.lr_number ? ` (LR ${l.lr_number})` : ""}.\nTrip: ${l.pickup_city} → ${l.delivery_city}.\nPlease open this link and allow GPS/location access to start live tracking: ${link}`;
      window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
      toast.success("Tracking link sent on WhatsApp");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-primary">Pipeline</div>
          <h1 className="font-heading font-black text-2xl sm:text-4xl tracking-tight">{isManager ? "Lead Management" : "My Leads"}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="rounded-none gap-2" onClick={copyQuickLink} data-testid="copy-quick-lead-link"><Link2 className="h-4 w-4" /> Quick Lead</Button>
          {isManager && (
            <>
              <ImportLeadsDialog onImported={load} />
              <SmartImportDialog onImported={load} />
              <Select value={assignTo} onValueChange={setAssignTo}>
                <SelectTrigger className="rounded-none w-48" data-testid="assign-select"><SelectValue placeholder="Assign to…" /></SelectTrigger>
                <SelectContent>{execs.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="outline" className="rounded-none gap-2" onClick={bulkAssign} disabled={!selected.length} data-testid="bulk-assign-btn"><UserCheck className="h-4 w-4" /> Assign ({selected.length})</Button>
              <Button className="rounded-none gap-2 font-bold uppercase tracking-wide" onClick={autoAssign} data-testid="auto-assign-btn"><Shuffle className="h-4 w-4" /> Auto-distribute</Button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="rounded-none pl-9" placeholder="Search Order No, LR No, name, phone…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="lead-search" />
        </div>
        <div className="flex flex-col">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">From</span>
          <Input type="date" className="rounded-none w-40" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="filter-start-date" />
        </div>
        <div className="flex flex-col">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">To</span>
          <Input type="date" className="rounded-none w-40" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="filter-end-date" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="rounded-none w-44" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="ghost" className="rounded-none" onClick={() => { setStartDate(monthStart()); setEndDate(todayStr()); setSearch(""); setStatus("all"); }} data-testid="clear-filters">Reset</Button>
        <Button variant={claimFilter ? "default" : "outline"} className={`rounded-none gap-2 text-xs font-bold uppercase tracking-wide ${claimFilter ? "bg-red-500 hover:bg-red-600 text-white" : "text-red-500 border-red-500/40 hover:bg-red-500/10"}`} onClick={() => setClaimFilter((v) => !v)} data-testid="leads-claim-filter"><AlertTriangle className="h-4 w-4" /> {claimFilter ? "Showing Damage Claims" : "Filter by Damage Claim"}</Button>
      </div>

      <div className="hidden md:block border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
            {isManager && <th className="py-3 px-3 w-8"><Checkbox checked={allChecked} onCheckedChange={(c) => setSelected(c ? leads.map((l) => l.id) : [])} data-testid="select-all" /></th>}
            <th className="px-4">Order</th><th className="px-4">Order Date</th><th className="px-4">LR Number</th><th className="px-4">Customer</th><th className="px-4">Category</th><th className="px-4">Route</th><th className="px-4 text-right">Article Value</th><th className="px-4">Assigned</th><th className="px-4">Status</th><th className="px-4">Call Status</th><th className="px-4 text-center">Share</th>
          </tr></thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={12} className="p-10 text-center text-muted-foreground">No orders match your filters.</td></tr>}
            {paged.map((l) => (
              <tr key={l.id} className="border-b border-border last:border-0 hover:bg-muted cursor-pointer transition-colors" data-testid={`lead-row-${l.order_number}`} onClick={() => setOpenId(l.id)}>
                {isManager && <td className="px-3" onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.includes(l.id)} onCheckedChange={() => toggle(l.id)} data-testid={`select-${l.order_number}`} /></td>}
                <td className="px-4 py-3 font-mono-num font-medium text-primary">{l.order_number}</td>
                <td className="px-4 text-xs text-muted-foreground whitespace-nowrap" data-testid={`lead-date-${l.order_number}`}>{l.created_at ? new Date(l.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—"}</td>
                <td className="px-4 font-mono-num text-xs">{l.lr_number || <span className="text-muted-foreground">—</span>}</td>
                <td className="px-4"><div className="font-medium">{l.customer_name}</div><div className="text-xs text-muted-foreground font-mono-num">{l.customer_phone}</div></td>
                <td className="px-4 capitalize text-muted-foreground">{l.category}</td>
                <td className="px-4 text-xs text-muted-foreground">{l.pickup_city} → {l.delivery_city}</td>
                <td className="px-4 text-right font-mono-num text-xs" data-testid={`lead-article-value-${l.order_number}`}>{l.article_value ? fmtInr(l.article_value) : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-4 text-xs">{l.assigned_to_name || <span className="text-amber-500">Unassigned</span>}</td>
                <td className="px-4"><WorkflowStatus lead={l} onOpen={(tab) => { setOpenId(l.id); setFocusStep(tab); }} /></td>
                <td className="px-4"><StatusBadge status={l.order_state === "confirmed_dispatched" ? "confirmed_dispatched" : l.status} /></td>
                <td className="px-4 text-center" onClick={(e) => e.stopPropagation()}>
                  <a href={waLink(l)} target="_blank" rel="noreferrer" title="Share on WhatsApp for vendor bids" className="inline-flex items-center justify-center h-8 w-8 border border-emerald-500/40 text-emerald-500 hover:bg-emerald-500 hover:text-white transition-colors" data-testid={`whatsapp-share-${l.order_number}`}>
                    <MessageCircle className="h-4 w-4" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr className="border-t-2 border-border bg-muted/50 font-bold">
            <td colSpan={isManager ? 7 : 6} className="px-4 py-3 text-right text-xs uppercase tracking-wide">Total Article Value ({shown.length} leads)</td>
            <td className="px-4 py-3 text-right font-mono-num" data-testid="leads-total-article-value">{fmtInr(totalArticleValue)}</td>
            <td colSpan={4} />
          </tr></tfoot>
        </table>
      </div>

      {/* Mobile status filter chips */}
      <div className="md:hidden flex gap-2 overflow-x-auto pb-3 -mx-4 px-4" data-testid="leads-chips">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`leads-chip-${s}`}
            className={`shrink-0 px-3 py-1.5 text-xs font-bold uppercase tracking-wide border rounded-full transition-colors ${status === s ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border"}`}
          >
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      {/* Mobile card view */}
      <div className="md:hidden space-y-3" data-testid="leads-cards">
        {shown.length === 0 && <div className="border border-border p-8 text-center text-muted-foreground">No orders match your filters.</div>}
        {paged.map((l) => {
          return (
            <div key={l.id} className="border border-border bg-card p-3 active:bg-muted transition-colors" data-testid={`lead-card-${l.order_number}`} onClick={() => setOpenId(l.id)}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-mono-num font-bold text-primary">{l.order_number}</div>
                  <div className="text-xs text-muted-foreground">{l.created_at ? new Date(l.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—"}{l.lr_number ? ` · LR ${l.lr_number}` : ""}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isManager && <span onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.includes(l.id)} onCheckedChange={() => toggle(l.id)} data-testid={`select-card-${l.order_number}`} /></span>}
                  <StatusBadge status={l.order_state === "confirmed_dispatched" ? "confirmed_dispatched" : l.status} />
                </div>
              </div>
              <div className="mt-2 font-medium">{l.customer_name} <span className="text-xs text-muted-foreground font-mono-num">· {l.customer_phone}</span></div>
              <div className="text-xs text-muted-foreground">{l.pickup_city} → {l.delivery_city} · <span className="capitalize">{l.category}</span></div>
              {!!l.article_value && <div className="text-xs mt-1">Article Value: <b className="font-mono-num">{fmtInr(l.article_value)}</b></div>}
              <div className="text-xs mt-1">{l.assigned_to_name ? <span className="text-muted-foreground">Assigned: {l.assigned_to_name}</span> : <span className="text-amber-500">Unassigned</span>}</div>
              <div className="mt-3"><WorkflowStatus lead={l} onOpen={(tab) => { setOpenId(l.id); setFocusStep(tab); }} /></div>
              <div className="flex items-center gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                <a href={waLink(l)} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center h-9 w-9 border border-emerald-500/40 text-emerald-500 shrink-0" data-testid={`whatsapp-card-${l.order_number}`}><MessageCircle className="h-4 w-4" /></a>
              </div>
            </div>
          );
        })}
      </div>

      <div className="md:hidden flex justify-between border border-border bg-muted/50 p-3 mt-3 text-sm font-bold" data-testid="leads-total-article-value-mobile">
        <span className="text-xs uppercase tracking-wide">Total Article Value ({shown.length})</span><span className="font-mono-num">{fmtInr(totalArticleValue)}</span>
      </div>

      <Pagination page={page} total={shown.length} pageSize={PAGE_SIZE} onPage={setPage} testid="leads-pagination" />

      <ShipmentStatusDialog lead={statusFor} onClose={() => setStatusFor(null)} onSaved={() => { setStatusFor(null); load(); }} />

      <LeadDrawer leadId={openId} focusStep={focusStep} onClose={() => { setOpenId(null); setFocusStep(null); }} onChange={load} />
    </div>
  );
}
